import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle, Database, ArrowRight, Server, Cloud } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export default function DatabaseSyncPage() {
  const [step, setStep] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState('');
  const [prodUrl, setProdUrl] = useState('https://foresightrx.com');
  const [progress, setProgress] = useState('');

  const collectionSummary = snapshot
    ? Object.entries(snapshot).map(([k, v]) => ({ name: k, count: Array.isArray(v) ? v.length : 0 })).filter(c => c.count > 0)
    : [];
  const totalDocs = collectionSummary.reduce((s, c) => s + c.count, 0);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      setProgress('Fetching all collections...');
      const res = await axios.get(`${API}/db/export-snapshot`, { withCredentials: true });
      setSnapshot(res.data);
      setStep(2);
      setProgress('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Export failed');
      setProgress('');
    } finally {
      setExporting(false);
    }
  };

  const handleSyncToProduction = async () => {
    setSyncing(true);
    setError('');
    setSyncResult(null);
    try {
      setProgress('Streaming preview → production via server-side proxy...');
      const targetUrl = prodUrl.replace(/\/$/, '');
      // Use the new server-side proxy endpoint to bypass browser CORS preflight
      // (production returns 405 on OPTIONS, which blocks direct browser POSTs).
      const res = await axios.post(`${API}/db/sync-to-production`, {
        target_url: targetUrl,
        admin_key: 'frx-sync-2026',
      }, {
        withCredentials: true,
        timeout: 600000,
      });
      setSyncResult(res.data?.production_response || res.data);
      setStep(3);
      setProgress('');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '';
      if (msg.includes('Could not reach production') || err?.code === 'ERR_NETWORK') {
        setError(`Cannot reach production: ${msg || 'no response'}`);
      } else {
        setError(`Sync failed: ${msg}`);
      }
      setProgress('');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadBackup = () => {
    if (!snapshot) return;
    const blob = new Blob([JSON.stringify(snapshot)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `foresightrx_snapshot_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="db-sync-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
          <Database className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Sync to Production</h1>
          <p className="text-xs text-slate-500">Push preview data to foresightrx.com in one click</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2">
        {[
          { num: 1, label: 'Export' },
          { num: 2, label: 'Sync' },
          { num: 3, label: 'Done' },
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            {i > 0 && <div className={`flex-1 h-px ${step > s.num - 1 ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              step === s.num ? 'bg-slate-900 text-white' :
              step > s.num ? 'bg-emerald-100 text-emerald-700' :
              'bg-slate-100 text-slate-400'
            }`}>
              {step > s.num ? <CheckCircle2 className="w-3 h-3" /> : <span>{s.num}</span>}
              <span>{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Export */}
      {step === 1 && (
        <Card className="border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-3">
            <Server className="w-4 h-4 text-sky-600" />
            <span className="text-sm font-semibold text-slate-800">Export Preview Database</span>
          </div>
          <CardContent className="p-5 space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This will create a snapshot of all CRM data from this preview environment — companies, contacts, assets, outreach records, and more.
            </p>
            <Button onClick={handleExport} disabled={exporting}
              className="w-full h-11 bg-[#0A1128] text-white hover:bg-slate-800 text-sm font-medium" data-testid="export-btn">
              {exporting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />{progress || 'Exporting...'}</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Export Snapshot</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Sync */}
      {step === 2 && (
        <>
          {/* Snapshot Summary */}
          <Card className="border border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-800">Snapshot Ready</span>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs">
                  {totalDocs.toLocaleString()} documents
                </Badge>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-1">
                {collectionSummary.map(c => (
                  <div key={c.name} className="flex items-center justify-between text-[11px]">
                    <span className="text-emerald-700 truncate">{c.name}</span>
                    <span className="font-mono text-emerald-600 ml-1">{c.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleDownloadBackup} className="text-[10px] text-emerald-600 hover:underline mt-2 block" data-testid="download-backup">
                Download backup file
              </button>
            </CardContent>
          </Card>

          {/* Sync Card */}
          <Card className="border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-3">
              <Cloud className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-slate-800">Push to Production</span>
            </div>
            <CardContent className="p-5 space-y-4">
              {/* Visual: Preview → Production */}
              <div className="flex items-center justify-center gap-3 py-3">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center mx-auto mb-1">
                    <Server className="w-5 h-5 text-sky-600" />
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">Preview</span>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-1">
                    <Cloud className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">Production</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1.5">Production URL</label>
                <input value={prodUrl} onChange={e => setProdUrl(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none"
                  data-testid="prod-url-input" />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  This will <strong>replace all data</strong> on production. The production deployment must be live first.
                </p>
              </div>

              <Button onClick={handleSyncToProduction} disabled={syncing}
                className="w-full h-11 bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium" data-testid="sync-btn">
                {syncing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />{progress || 'Syncing...'}</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Sync to Production</>
                )}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 3: Done */}
      {step === 3 && syncResult && (
        <Card className="border border-emerald-200 bg-emerald-50/30 overflow-hidden">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Production Synced
            </h2>
            <p className="text-sm text-slate-500 mb-4">All data has been pushed to {prodUrl}</p>
            <div className="bg-white rounded-lg border border-emerald-200 p-4 text-left max-w-md mx-auto">
              <p className="text-xs font-semibold text-slate-700 mb-2">Import Summary</p>
              <div className="grid grid-cols-2 gap-1">
                {Object.entries(syncResult.results || {}).filter(([,v]) => !v.includes('skipped')).map(([k, v]) => (
                  <span key={k} className="text-[10px] text-emerald-600">{k}: {v}</span>
                ))}
              </div>
            </div>
            <Button onClick={() => { setStep(1); setSnapshot(null); setSyncResult(null); }} variant="outline" size="sm" className="mt-4" data-testid="reset-btn">
              Start Over
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-rose-700">Sync Failed</p>
            <p className="text-xs text-rose-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}
